import { CheckCircle2, KeyRound, LockKeyhole, Mail, UserRound } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { apiRequest } from '../api/client'
import type { User } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useOnlineStatus } from '../auth/useOnlineStatus'
import { Button } from '../components/ui'
import { errorMessage } from '../utils/format'

function AccessFrame({ children, title, description }: { children: ReactNode; title: string; description: string }) {
  return <main className="login-page">
    <section className="login-intro">
      <div className="brand brand--light"><span className="brand__mark"><img src="/brand/logo.png" alt="" width="36" height="36" /></span><span><strong>Управление ТОиР</strong><small>ООО «БТТ-ГРУПП»</small></span></div>
      <div className="login-intro__content"><p className="eyebrow eyebrow--light">Рабочий доступ</p><h1>{title}</h1><p>{description}</p></div>
    </section>
    <section className="login-panel">{children}</section>
  </main>
}

export function RegistrationPage() {
  const { user } = useAuth()
  const online = useOnlineStatus()
  const navigate = useNavigate()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  if (user) return <Navigate to="/" replace />

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (password !== confirmation) { setError('Пароли не совпадают. Проверьте оба поля.'); return }
    setSubmitting(true)
    setError('')
    try {
      await apiRequest<User>('/auth/register', { method: 'POST', body: { fullName: fullName.trim(), email: email.trim(), password }, authToken: null })
      navigate('/login', { replace: true, state: { message: 'Заявка на доступ отправлена. Вход станет доступен после подтверждения администратором вашей организации.' } })
    } catch (caught) { setError(errorMessage(caught)) }
    finally { setSubmitting(false) }
  }

  return <AccessFrame title="Запросите рабочий доступ" description="Администратор вашей организации проверит заявку и назначит рабочие роли. После подтверждения вы сможете войти.">
    <form className="login-form" onSubmit={(event) => void submit(event)}>
      <div><p className="eyebrow">Регистрация</p><h2>Заявка на доступ</h2><p>Используйте рабочую почту и укажите настоящее имя.</p></div>
      {!online && <div className="form-message form-message--warning" role="alert">Подключитесь к сети, чтобы отправить заявку.</div>}
      {error && <div className="form-message form-message--error" role="alert">{error}</div>}
      <label className="field"><span>Фамилия и имя</span><div className="field__control"><UserRound size={18} /><input name="fullName" value={fullName} onChange={(event) => setFullName(event.target.value)} required autoComplete="name" maxLength={255} /></div></label>
      <label className="field"><span>Рабочая почта</span><div className="field__control"><Mail size={18} /><input name="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" maxLength={180} /></div></label>
      <label className="field"><span>Пароль</span><div className="field__control"><LockKeyhole size={18} /><input name="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="new-password" minLength={10} maxLength={200} /></div><small>Не менее 10 символов, хотя бы одна буква и одна цифра.</small></label>
      <label className="field"><span>Повторите пароль</span><div className="field__control"><LockKeyhole size={18} /><input name="passwordConfirmation" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required autoComplete="new-password" /></div></label>
      <Button type="submit" loading={submitting} disabled={!online || !fullName.trim() || !email.trim() || !password || !confirmation}>Отправить заявку</Button>
      <div className="login-links"><Link to="/login">Вернуться ко входу</Link></div>
    </form>
  </AccessFrame>
}

export function RecoverAccessPage() {
  const { user } = useAuth()
  const online = useOnlineStatus()
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  if (user) return <Navigate to="/" replace />

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (token && password !== confirmation) { setError('Пароли не совпадают. Проверьте оба поля.'); return }
    setSubmitting(true)
    setError('')
    try {
      const result = token
        ? await apiRequest<{ message: string }>('/auth/password/reset', { method: 'POST', body: { token, password }, authToken: null })
        : await apiRequest<{ message: string }>('/auth/password/forgot', { method: 'POST', body: { email: email.trim() }, authToken: null })
      setMessage(result.message)
    } catch (caught) { setError(errorMessage(caught)) }
    finally { setSubmitting(false) }
  }

  return <AccessFrame title={token ? 'Задайте новый пароль' : 'Восстановите доступ'} description={token ? 'После сохранения новый пароль сразу можно использовать для входа.' : 'Мы отправим одноразовую ссылку на рабочую почту. Она действует 30 минут.'}>
    <form className="login-form" onSubmit={(event) => void submit(event)}>
      <div><p className="eyebrow">Восстановление доступа</p><h2>{token ? 'Новый пароль' : 'Получить ссылку'}</h2><p>{token ? 'Придумайте пароль, который раньше не использовали.' : 'Укажите почту, связанную с учётной записью.'}</p></div>
      {message && <div className="form-message form-message--success" role="status"><CheckCircle2 size={18} /><span>{message}</span></div>}
      {error && <div className="form-message form-message--error" role="alert">{error}</div>}
      {!online && !message && <div className="form-message form-message--warning" role="alert">Подключитесь к сети, чтобы восстановить доступ.</div>}
      {!message && (token ? <>
        <label className="field"><span>Новый пароль</span><div className="field__control"><KeyRound size={18} /><input name="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="new-password" minLength={10} maxLength={200} /></div><small>Не менее 10 символов, хотя бы одна буква и одна цифра.</small></label>
        <label className="field"><span>Повторите пароль</span><div className="field__control"><LockKeyhole size={18} /><input name="passwordConfirmation" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required autoComplete="new-password" /></div></label>
      </> : <label className="field"><span>Рабочая почта</span><div className="field__control"><Mail size={18} /><input name="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" maxLength={180} /></div></label>)}
      {!message && <Button type="submit" loading={submitting} disabled={!online || (token ? !password || !confirmation : !email.trim())}>{token ? 'Сохранить новый пароль' : 'Отправить ссылку'}</Button>}
      <div className="login-links"><Link to="/login">Вернуться ко входу</Link>{token && error && <Link to="/recover-access">Запросить новую ссылку</Link>}{!token && <Link to="/register">Запросить доступ</Link>}</div>
    </form>
  </AccessFrame>
}
